// ---------------------------------------------------------------------------
// C5: Conversion Score Calculator — live scoring of page quality
// ---------------------------------------------------------------------------
// 10 factors, weighted to 100. Updates in real-time as user edits.
// CTA(15), social proof(12), form friction(12), urgency(10), headline(10),
// hierarchy(10), mobile(10), speed(8), trust(8), congruence(5) = 100
// ---------------------------------------------------------------------------

export interface ConversionFactor {
  id: string;
  name: string;
  weight: number;
  score: number; // 0-1 (multiplied by weight for final)
  issues: string[];
  suggestions: string[];
}

export interface ConversionScore {
  total: number; // 0-100
  factors: ConversionFactor[];
  topIssue: string | null;
}

interface PuckComponent { type: string; props: Record<string, unknown>; }

/**
 * Calculate conversion score for a page. Pure function — no side effects.
 * Call on every edit for real-time scoring.
 */
export function calculateConversionScore(components: PuckComponent[]): ConversionScore {
  const factors: ConversionFactor[] = [
    scoreCTA(components),
    scoreSocialProof(components),
    scoreFormFriction(components),
    scoreUrgency(components),
    scoreHeadline(components),
    scoreHierarchy(components),
    scoreMobile(components),
    scoreSpeed(components),
    scoreTrust(components),
    scoreCongruence(components),
  ];

  const total = Math.round(factors.reduce((sum, f) => sum + f.score * f.weight, 0));
  const worst = factors.reduce((w, f) => f.score < w.score ? f : w, factors[0]!);

  return { total, factors, topIssue: worst.issues[0] ?? null };
}

// --- Factor Scorers ---

function scoreCTA(components: PuckComponent[]): ConversionFactor {
  const issues: string[] = [];
  const suggestions: string[] = [];
  const ctaComponents = components.filter(c => c.type === "cta" || c.type === "hero" || c.props.buttonText);
  const hasCTA = ctaComponents.length > 0;
  const ctaAboveFold = components.findIndex(c => c.props.buttonText) < 3; // First 3 components = above fold

  let score = 0;
  if (hasCTA) score += 0.4;
  if (ctaAboveFold) score += 0.3;
  if (ctaComponents.length >= 2) score += 0.15; // Multiple CTAs
  if (ctaComponents.some(c => String(c.props.buttonText ?? "").length > 3 && String(c.props.buttonText ?? "").length < 30)) score += 0.15;

  if (!hasCTA) { issues.push("No CTA button found"); suggestions.push("Add a clear call-to-action button"); }
  if (!ctaAboveFold && hasCTA) { issues.push("CTA is below the fold"); suggestions.push("Move your primary CTA above the fold"); }

  return { id: "cta", name: "CTA Placement", weight: 15, score: Math.min(1, score), issues, suggestions };
}

function scoreSocialProof(components: PuckComponent[]): ConversionFactor {
  const issues: string[] = [];
  const suggestions: string[] = [];
  const hasTestimonials = components.some(c => c.type === "testimonials" || c.type === "reviews" || c.type === "social-proof");
  const hasLogos = components.some(c => c.type === "logos" || c.type === "trusted-by" || c.type === "partners");
  const hasStats = components.some(c => c.type === "stats" || c.type === "numbers" || c.type === "metrics");

  let score = 0;
  if (hasTestimonials) score += 0.5;
  if (hasLogos) score += 0.25;
  if (hasStats) score += 0.25;

  if (!hasTestimonials) { issues.push("No testimonials or reviews"); suggestions.push("Add customer testimonials"); }
  if (!hasLogos) { suggestions.push("Add trusted-by logos"); }

  return { id: "social_proof", name: "Social Proof", weight: 12, score, issues, suggestions };
}

function scoreFormFriction(components: PuckComponent[]): ConversionFactor {
  const issues: string[] = [];
  const suggestions: string[] = [];
  const forms = components.filter(c => c.type === "form" || c.type === "contact-form" || c.type === "lead-form");

  let score = 1; // No form = no friction = perfect
  for (const form of forms) {
    const fields = (form.props.fields as unknown[])?.length ?? 0;
    if (fields > 4) { score = 0.3; issues.push(`Form has ${fields} fields (>4 increases abandonment)`); suggestions.push("Reduce to 3-4 essential fields"); }
    else if (fields > 2) score = 0.7;
    else score = 1;
  }

  return { id: "form_friction", name: "Form Friction", weight: 12, score, issues, suggestions };
}

function scoreUrgency(components: PuckComponent[]): ConversionFactor {
  const issues: string[] = [];
  const suggestions: string[] = [];
  const hasCountdown = components.some(c => c.type === "countdown" || c.type === "timer");
  const hasLimitedOffer = components.some(c => {
    const text = JSON.stringify(c.props).toLowerCase();
    return text.includes("limited") || text.includes("expires") || text.includes("today only") || text.includes("hurry");
  });

  let score = 0;
  if (hasCountdown) score += 0.6;
  if (hasLimitedOffer) score += 0.4;

  if (score === 0) { issues.push("No urgency elements"); suggestions.push("Add a countdown timer or limited-time offer"); }

  return { id: "urgency", name: "Urgency", weight: 10, score, issues, suggestions };
}

function scoreHeadline(components: PuckComponent[]): ConversionFactor {
  const issues: string[] = [];
  const suggestions: string[] = [];
  const hero = components.find(c => c.type === "hero" || c.type === "header");
  const heading = String(hero?.props.heading ?? hero?.props.title ?? "");

  let score = 0;
  if (heading.length > 0) score += 0.3;
  if (heading.length > 10 && heading.length < 80) score += 0.3;
  if (/benefit|save|grow|increase|boost|transform/i.test(heading)) score += 0.2; // Benefit-oriented
  if (/\d/.test(heading)) score += 0.2; // Contains a number (specificity)

  if (!heading) { issues.push("No headline found"); suggestions.push("Add a compelling headline to your hero section"); }
  if (heading.length > 80) { issues.push("Headline too long (>80 chars)"); suggestions.push("Shorten to under 80 characters"); }

  return { id: "headline", name: "Headline", weight: 10, score: Math.min(1, score), issues, suggestions };
}

function scoreHierarchy(components: PuckComponent[]): ConversionFactor {
  const issues: string[] = [];
  const suggestions: string[] = [];
  const types = components.map(c => c.type);
  const hasHero = types.some(t => t === "hero" || t === "header");
  const hasFooter = types.some(t => t === "footer");
  const heroFirst = types[0] === "hero" || types[0] === "header";
  const hasSections = components.length >= 3;

  let score = 0;
  if (hasHero) score += 0.3;
  if (heroFirst) score += 0.2;
  if (hasFooter) score += 0.2;
  if (hasSections) score += 0.3;

  if (!hasHero) { issues.push("No hero section"); suggestions.push("Start with a hero section"); }
  if (!heroFirst && hasHero) { issues.push("Hero is not the first section"); suggestions.push("Move hero to the top"); }

  return { id: "hierarchy", name: "Visual Hierarchy", weight: 10, score, issues, suggestions };
}

function scoreMobile(components: PuckComponent[]): ConversionFactor {
  // Heuristic: assume responsive if using standard Puck components
  // Flag components with fixed widths or complex grids
  const issues: string[] = [];
  const fixedWidth = components.filter(c => {
    const props = JSON.stringify(c.props);
    return props.includes("px") && !props.includes("max-width");
  });

  let score = fixedWidth.length === 0 ? 1 : Math.max(0, 1 - fixedWidth.length * 0.2);
  if (fixedWidth.length > 0) { issues.push(`${fixedWidth.length} components may have fixed widths`); }

  return { id: "mobile", name: "Mobile Ready", weight: 10, score, issues, suggestions: [] };
}

function scoreSpeed(components: PuckComponent[]): ConversionFactor {
  // Heuristic: fewer components = faster. Heavy components (video, maps) penalized.
  const heavy = components.filter(c => ["video", "map", "iframe", "embed"].includes(c.type));
  const tooMany = components.length > 8;

  let score = 1;
  if (heavy.length > 0) score -= heavy.length * 0.15;
  if (tooMany) score -= 0.2;

  const issues: string[] = [];
  if (heavy.length > 0) issues.push(`${heavy.length} heavy elements (video/map) may slow loading`);
  if (tooMany) issues.push("Page has many sections — may increase load time");

  return { id: "speed", name: "Speed", weight: 8, score: Math.max(0, score), issues, suggestions: [] };
}

function scoreTrust(components: PuckComponent[]): ConversionFactor {
  const issues: string[] = [];
  const hasGuarantee = components.some(c => JSON.stringify(c.props).toLowerCase().includes("guarantee"));
  const hasPrivacy = components.some(c => JSON.stringify(c.props).toLowerCase().includes("privacy"));
  const hasBadges = components.some(c => c.type === "trust-badges" || c.type === "security-badges");

  let score = 0;
  if (hasGuarantee) score += 0.4;
  if (hasPrivacy) score += 0.3;
  if (hasBadges) score += 0.3;

  if (score === 0) { issues.push("No trust signals"); }
  return { id: "trust", name: "Trust Signals", weight: 8, score, issues, suggestions: ["Add trust badges, guarantee, or privacy mention"] };
}

function scoreCongruence(components: PuckComponent[]): ConversionFactor {
  // Check if CTA text matches the headline promise
  const hero = components.find(c => c.type === "hero");
  const cta = components.find(c => c.props.buttonText);
  const heading = String(hero?.props.heading ?? "").toLowerCase();
  const button = String(cta?.props.buttonText ?? "").toLowerCase();

  let score = 0.5; // Neutral if can't determine
  if (heading && button) {
    // Simple keyword overlap check
    const headingWords = heading.split(/\s+/);
    const overlap = headingWords.some(w => w.length > 3 && button.includes(w));
    score = overlap ? 1 : 0.4;
  }

  return { id: "congruence", name: "Message Congruence", weight: 5, score, issues: [], suggestions: [] };
}
